import React, { useState, useEffect, useRef } from "react";

const BAN_URL = "https://api-adresse.data.gouv.fr/search/";

/**
 * Champ adresse avec autocomplétion via l'API Adresse (BAN) du gouvernement français.
 *
 * Props :
 *   label      — libellé du champ
 *   icon       — icône JSX à gauche
 *   value      — valeur courante (string)
 *   onChange   — appelé comme un input standard : (e) => setState(e.target.value)
 *   onSelect   — appelé quand une suggestion est choisie :
 *                ({ label, street, postcode, city, context })
 *   placeholder, required, disabled
 */
export function AddressAutocomplete({
  label,
  icon,
  value = "",
  onChange,
  onSelect,
  placeholder = "Ex : 10 rue de la Paix, Paris",
  required = false,
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const debounceRef  = useRef(null);
  const containerRef = useRef(null);

  /* Fermer en cliquant en dehors */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Appel API avec debounce */
  const handleChange = (e) => {
    const val = e.target.value;
    onChange?.(e);
    setActiveIdx(-1);

    clearTimeout(debounceRef.current);

    if (!val || val.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res  = await fetch(`${BAN_URL}?q=${encodeURIComponent(val)}&limit=6&autocomplete=1`);
        const data = await res.json();
        const feats = (data.features || []).filter(
          (f) => f.properties?.label
        );
        setSuggestions(feats);
        setOpen(feats.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 350);
  };

  /* Sélection d'une suggestion */
  const handleSelect = (feat) => {
    const p = feat.properties;
    const syntheticEvt = { target: { value: p.label } };
    onChange?.(syntheticEvt);
    onSelect?.({
      label:    p.label,
      street:   p.name   || p.label,
      postcode: p.postcode || "",
      city:     p.city   || "",
      context:  p.context || "",
    });
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
  };

  /* Navigation clavier */
  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  return (
    <div ref={containerRef} className="addrWrapper">
      <label className="field">
        <div className="fieldLabel">{label}{required && " *"}</div>
        <div className="input">
          <span className="inputLeft">{icon}</span>
          <input
            className="inputControl"
            type="text"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
          />
        </div>
      </label>

      {open && suggestions.length > 0 && (
        <ul className="addrDropdown" role="listbox">
          {suggestions.map((feat, idx) => {
            const p = feat.properties;
            return (
              <li
                key={idx}
                role="option"
                aria-selected={idx === activeIdx}
                className={`addrOption ${idx === activeIdx ? "addrOptionActive" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(feat); }}
              >
                <span className="addrOptionMain">{p.name}</span>
                <span className="addrOptionSub">
                  {p.postcode} {p.city}
                  {p.context ? ` — ${p.context.split(",")[0]}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
